/// <amd-module name="Analytics/Shared/Utils/Number" />
import Culture = require("Analytics/Shared/Utils/Culture");
export function getPercentage(totalCount: number, count: number): number {
    if (totalCount === 0) {
        return 0;
    }

    const percentage = Math.round((count / totalCount) * 10000) / 100;

    if (count > 0 && percentage === 0) {
        return 0.01;
    } else if (count < totalCount && percentage === 100) {
        return 99.99;
    }

    return percentage;
}

export function getPercentageVariation(prevValue: number, currentValue: number): number {
    const diff: number = Math.abs(prevValue - currentValue);
    return getPercentage(prevValue, diff);
}

function zeroPad(str: string, count: number, left: boolean): string {
    for (let l = str.length; l < count; l++) {
        str = left ? "0" + str : str + "0";
    }
    return str;
}

/**
 * Converts this number to a string in the current culture's locale
 * without specifying a precision. So, for example, with Spanish culture,
 * (3) gets translated to "3", and (3.1416) becomes "3,1416". The jQuery's
 * localeFormat requires a precision (the default is "2" if not specified).
 * So 3.localeFormat("N") become "3,00".
 *
 * @param num  The Number to format
 * @param includeGroupSeparators If true, use locale-specific
 * group separators (i.e. 3,000) in the output
 * @param cultureInfo Culture info (CurrentCulture if not specified)
 * @return
 */
export function toDecimalLocaleString(num: number, includeGroupSeparators?: boolean, cultureInfo?: Culture.ICultureInfo): string {
    let exponent: number;
    let nf: any; // TODO: If we end up typing MSAjax we can get the actual numberFormat object type here
    let split: string[];
    let numberString = num.toString();
    let right = "";
    let groupSizes: number[];
    let sep: string;
    let curSize: number;
    let curGroupIndex: number;
    let stringIndex: number;
    let ret: string;

    if (cultureInfo) {
        nf = cultureInfo.numberFormat;
    } else {
        nf = Culture.getNumberFormat();
    }

    split = numberString.split(/e/i);
    numberString = split[0];
    exponent = split.length > 1 ? parseInt(split[1], 10) : 0;
    split = numberString.split(".");
    numberString = split[0];
    right = split.length > 1 ? split[1] : "";

    if (exponent > 0) {
        right = zeroPad(right, exponent, false);
        numberString += right.slice(0, exponent);
        right = right.substr(exponent);
    } else if (exponent < 0) {
        exponent = -exponent;
        numberString = zeroPad(numberString, exponent + 1, true);
        right = numberString.slice(-exponent, numberString.length) + right;
        numberString = numberString.slice(0, -exponent);
    }

    if (right.length > 0) {
        right = nf.NumberDecimalSeparator + right;
    }

    if (includeGroupSeparators === true) {
        groupSizes = nf.NumberGroupSizes;
        sep = nf.NumberGroupSeparator;
        curSize = groupSizes[0];
        curGroupIndex = 1;
        stringIndex = numberString.length - 1;
        ret = "";

        while (stringIndex >= 0) {
            if (curSize === 0 || curSize > stringIndex) {
                if (ret.length > 0) {
                    return numberString.slice(0, stringIndex + 1) + sep + ret + right;
                } else {
                    return numberString.slice(0, stringIndex + 1) + right;
                }
            }
            if (ret.length > 0) {
                ret = numberString.slice(stringIndex - curSize + 1, stringIndex + 1) + sep + ret;
            } else {
                ret = numberString.slice(stringIndex - curSize + 1, stringIndex + 1);
            }
            stringIndex -= curSize;
            if (curGroupIndex < groupSizes.length) {
                curSize = groupSizes[curGroupIndex];
                curGroupIndex++;
            }
        }
        return numberString.slice(0, stringIndex + 1) + sep + ret + right;
    } else {
        return numberString + right;
    }
}

/**
 * Format a given number to the AbbreviatedShortForm with the given locale.
 * For example (US) 10,000 -> 10k ; 9,200 -> 9.2K (JA) 10,000 -> 1万 ; 9,200 -> 9.2千
 * @param count - the number to format with
 * @param cultureInfo - the cultureInfo, use user's default if not given.
 */
export function formatAbbreviatedNumber(count: number, cultureInfo?: Culture.ICultureInfo): string {
    let summarizedValue: string;
    let numberShortForm: Culture.INumberShortForm;
    let decimalSeparator: string;

    // Format a number to desired precision, and apply a string format to it (for Numerical suffix)
    const formatNumber = (numberSymbol: string, value: number, decimalCount: number, decimalSep: string): string => {
        if (numberSymbol) {
            const formattedNumber = value.toFixed(decimalCount);
            return (formattedNumber + numberSymbol).replace(".", decimalSep);
        } else {
            return toDecimalLocaleString(value, false);
        }
    };
    summarizedValue = "";
    if (cultureInfo) {
        numberShortForm = cultureInfo.numberShortForm;
        decimalSeparator = cultureInfo.numberFormat.NumberDecimalSeparator;
    } else {
        numberShortForm = Culture.getNumberShortForm();
        decimalSeparator = Culture.getNumberFormat().NumberDecimalSeparator;
    }

    // Pack a string which has a maximum size of 3 characters + a suffix. This means are only able to allow a decimal in the case of a lone whole number digit
    // If we have a number larger than a trillion, that's unsupported for forseeable future and can be dealt with as a feature request when someone run's into
    // use cases for larger values.
    if (count < 1000) {
        summarizedValue = formatNumber("", count, 0, "");
    } else if (count / 1000 < 10) {
        summarizedValue = formatNumber(numberShortForm.ThousandSymbol, count / 1000, 1, decimalSeparator);
    }

    // We would use the shortForm logic to check
    if (count / numberShortForm.NumberGroupSize > 1) {
        for (let i = 0; i < numberShortForm.QuantitySymbols.length; i++) {
            const divider = Math.pow(numberShortForm.NumberGroupSize, i + 1);
            if (count / divider < 10) {
                summarizedValue = formatNumber(numberShortForm.QuantitySymbols[i], count / divider, 1, decimalSeparator);
                break;
            } else if (count / divider < numberShortForm.NumberGroupSize) {
                summarizedValue = formatNumber(numberShortForm.QuantitySymbols[i], count / divider, 0, "");
                break;
            }
        }

        // If we don't have a number, then it is too big to handle
        if (summarizedValue == "") {
            summarizedValue = count.toExponential(0);
        }
    }

    return summarizedValue;
}
